/**
 * Modale d'import d'une fiche réglementaire.
 *
 * Deux modes possibles (toggle en haut) :
 *   1. Par UFI    — saisie manuelle d'un code UFI XXXX-XXXX-XXXX-XXXX
 *   2. Par client — arborescence client → gamme → produit fini, clic
 *                   sur un produit fini → import
 *
 * Workflow :
 *   - Vérification du token API au mount (sinon affiche un warning)
 *   - L'import génère N calques (pictos + mentions) sur le canvas
 *   - Si un bloc avec le même blockId existait déjà, il est remplacé
 */
import React from 'react';
import { useLabelStore } from '../store/useLabelStore';
import {
  fetchRegulatoryByUfi,
  fetchCatalogClients,
  fetchCatalogClient,
  getTokenStatus,
} from '../api/regulatoryClient';
import {
  buildRegulatoryLayers,
  produitFiniToPayload,
  resolveLabelForCountry,
} from '../utils/regulatoryBlockBuilder';
import { reconcileImportedLayers } from '../utils/regulatoryTemplate';
import { fitElementsToContent } from '../utils/fitFrame';
import RegulatoryCatalogPicker from './RegulatoryCatalogPicker';


/**
 * @param {object} props
 * @param {(type:'ok'|'warn'|'err', texte:string)=>void} [props.onDone] - compte
 *   rendu de l'import, affiché par l'éditeur. Un import peut RETIRER des calques
 *   (mentions du produit précédent absentes de la nouvelle fiche) : le taire
 *   serait faire disparaître des mentions réglementaires sans un mot.
 */
export default function RegulatoryImportModal({ isOpen, onClose, onDone }) {
  const { canvas, addElement, elements, removeElements, patchElements } = useLabelStore();
  const [mode, setMode] = React.useState('ufi'); // 'ufi' | 'catalog'
  const [ufi, setUfi] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [tokenConfigured, setTokenConfigured] = React.useState(null);

  // Catalog state (chargé à la demande quand on passe en mode 'catalog')
  const [catalog, setCatalog] = React.useState(null);
  const [catalogLoading, setCatalogLoading] = React.useState(false);
  const [catalogError, setCatalogError] = React.useState(null);
  // État de chargement PAR CLIENT : `{ [id]: { loading, error } }`. La liste des
  // clients arrive sans produits ; chaque client se charge au dépliage.
  const [clientStates, setClientStates] = React.useState({});
  // Garde anti-boucle : ref qui survit aux re-renders et marque qu'on a
  // déjà tenté un fetch (succès OU échec). Évite de refetch en boucle
  // quand l'API renvoie une erreur — l'utilisateur doit cliquer sur
  // "Réessayer" explicitement.
  const catalogFetchRef = React.useRef(false);

  // Reset à chaque ouverture
  React.useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setUfi('');
    setMode('ufi');
    setCatalog(null);
    setCatalogError(null);
    setClientStates({});
    catalogFetchRef.current = false;
    // Remise à `null` = « en cours de vérification » : sans elle, une réouverture
    // repartait avec le verdict de la fois précédente pendant tout l'appel.
    setTokenConfigured(null);
    getTokenStatus()
      .then(s => setTokenConfigured(!!s.configured))
      .catch(() => setTokenConfigured(false));
  }, [isOpen]);

  const loadCatalog = React.useCallback(() => {
    if (catalogLoading) return;
    catalogFetchRef.current = true;
    setCatalogLoading(true);
    setCatalogError(null);
    // Deux temps (spécification FDS Studio du 16/09/2026) : la LISTE des clients
    // ici, légère ; les gammes et produits d'un client dans `loadClient`, au
    // dépliage. L'appel unique `/catalog` qui renvoyait tout en plusieurs Mo est
    // celui qui est resté en panne du 11 au 13 septembre.
    fetchCatalogClients()
      .then((c) => { setCatalog(c); setClientStates({}); })
      .catch((e) => setCatalogError(e.message || 'Impossible de charger l\'arborescence.'))
      .finally(() => setCatalogLoading(false));
  }, [catalogLoading]);

  /**
   * Charge UN client — ses gammes et produits, étiquettes comprises — et le
   * substitue à son entrée légère dans la liste. Un 404 veut dire que la liste
   * est périmée (client supprimé côté FDS) : on la recharge entière plutôt que
   * d'afficher une erreur sur un client qui n'existe plus.
   */
  const loadClient = React.useCallback((id, { force = false } = {}) => {
    if (!id) return;
    setClientStates((s) => {
      if (s[id]?.loading && !force) return s;
      return { ...s, [id]: { loading: true, error: null } };
    });
    fetchCatalogClient(id)
      .then((detail) => {
        setCatalog((c) => ({
          ...(c || {}),
          clients: (c?.clients || []).map((cl) => (cl.id === id ? { ...cl, ...detail } : cl)),
        }));
        setClientStates((s) => ({ ...s, [id]: { loading: false, error: null } }));
      })
      .catch((e) => {
        if (e.status === 404) {
          catalogFetchRef.current = false;
          setCatalog(null);
          setClientStates({});
          loadCatalog();
          return;
        }
        setClientStates((s) => ({ ...s, [id]: { loading: false, error: e.message || 'Impossible de charger ce client.' } }));
      });
  }, [loadCatalog]);

  // Chargement du catalog uniquement à la 1ère bascule vers 'catalog'.
  // Pas de boucle possible : la ref empêche tout re-fetch automatique.
  React.useEffect(() => {
    if (mode !== 'catalog' || !tokenConfigured || catalogFetchRef.current) return;
    loadCatalog();
  }, [mode, tokenConfigured, loadCatalog]);

  if (!isOpen) return null;

  // TROIS états, pas deux. `tokenConfigured` vaut `null` tant que getTokenStatus()
  // est en vol : avant, ce `null` passait pour « jeton présent » — aucun bandeau,
  // bouton d'import offert, et l'import partait droit vers l'échec alors qu'aucun
  // jeton n'était configuré.
  const checkingToken = tokenConfigured === null;
  const noToken = tokenConfigured === false;
  const tokenReady = tokenConfigured === true;

  /**
   * Le bandeau rouge est PARTAGÉ par les deux modes : sans remise à zéro de
   * `error`, un « Saisissez un UFI » restait affiché après bascule vers le
   * catalogue, où il ne voulait plus rien dire.
   */
  const switchMode = (next) => {
    if (next === mode) return;
    setError(null);
    setMode(next);
  };

  /**
   * Insère les calques générés en les CALANT sur les calques réglementaires
   * déjà présents (squelette d'un modèle .lfgr, ou import précédent) : matching
   * par RÔLE → le placement + la mise en forme existants sont conservés, seul le
   * contenu est mis à jour. Évite la superposition de doublons.
   */
  const insertLayers = (layers) => {
    const { adds, updates, removeIds, staleIds, conserves } = reconcileImportedLayers(layers, elements, { canvas });

    // 1. Les calques du produit PRÉCÉDENT que cette fiche ne fournit plus, et
    //    ceux qu'il faut remplacer parce que leur type a changé. Le retrait passe
    //    en premier : `removeElements` empile l'instantané d'annulation AVANT de
    //    supprimer, donc un Ctrl+Z ramène l'étiquette entière d'avant l'import.
    const aRetirer = [...removeIds, ...staleIds];
    if (aRetirer.length > 0) removeElements(aRetirer);

    // 2. Les calques conservés : mise à jour SUR PLACE. Ils gardent leur groupe,
    //    leur rang dans la pile, leur verrou et leur masquage — ce que la
    //    destruction-recréation d'avant leur faisait perdre à chaque import.
    const ids = updates.map((u) => u.id);
    if (updates.length > 0) {
      patchElements(Object.fromEntries(updates.map((u) => [u.id, u.partial])));
    }

    // 3. Les calques vraiment nouveaux. Les identifiants sont attribués ICI, et
    //    non laissés à `addElement`, parce que l'ajustement automatique qui suit
    //    doit savoir QUELS calques mesurer.
    for (const l of adds) {
      const id = crypto.randomUUID();
      ids.push(id);
      addElement({ ...l, id });
    }

    // Ajustement du cadre au contenu, une fois les calques peints. Volontairement
    // non attendu : la modale se ferme tout de suite, l'ajustement se pose seul
    // deux images plus tard (cf. utils/fitFrame). Un échec de mesure laisse
    // simplement les cadres tels quels.
    fitElementsToContent(ids, { nomHistorique: 'Import réglementaire · ajusté au contenu' })
      .catch(() => {});

    return { ajoutes: adds.length, misAJour: updates.length, retires: staleIds.length, gardes: (conserves || []).length };
  };

  /** Dit ce que l'import a fait, retraits compris. */
  const rendreCompte = ({ ajoutes, misAJour, retires, gardes }, quoi) => {
    if (!onDone) return;
    const bouts = [];
    if (ajoutes) bouts.push(`${ajoutes} calque${ajoutes > 1 ? 's' : ''} ajouté${ajoutes > 1 ? 's' : ''}`);
    if (misAJour) bouts.push(`${misAJour} mis à jour`);
    if (retires) bouts.push(`${retires} retiré${retires > 1 ? 's' : ''} (mention${retires > 1 ? 's' : ''} absente${retires > 1 ? 's' : ''} de cette fiche)`);
    // Une fiche sans zones `print` ne met pas à jour les mentions que seules les
    // zones posent : elles restent telles quelles, et on le dit (relecture de #336).
    if (gardes) bouts.push(`${gardes} gardée${gardes > 1 ? 's' : ''} telle${gardes > 1 ? 's' : ''} quelle${gardes > 1 ? 's' : ''} (fiche sans zones prêtes à imprimer)`);
    onDone(retires || gardes ? 'warn' : 'ok', `${quoi} importé · ${bouts.join(', ') || 'aucun changement'}.`);
  };

  const handleImportUfi = async () => {
    setError(null);
    // La touche Entrée ne regardait que `loading` : elle pouvait lancer un import
    // avant la fin de la vérification du jeton, ou sans jeton du tout.
    if (checkingToken) {
      setError("Vérification du jeton API FDS en cours : patientez un instant avant de lancer l'import.");
      return;
    }
    if (noToken) {
      setError("Le jeton API FDS n'est pas configuré : renseignez-le dans Préférences → Récupération informations réglementaires.");
      return;
    }
    const cleaned = ufi.trim().toUpperCase();
    if (!cleaned) {
      setError('Saisissez un UFI au format XXXX-XXXX-XXXX-XXXX.');
      return;
    }
    setLoading(true);
    try {
      const payload = await fetchRegulatoryByUfi(cleaned);
      // Import par UFI = variante par défaut (`label`, ou 1ʳᵉ variante de
      // `labels`). Le choix d'un pays précis (FR/CH/BE) se fait dans l'arbre
      // « Par client » en cliquant le bouton-pays du produit.
      payload.label = resolveLabelForCountry(payload, null);
      const layers = buildRegulatoryLayers(payload, canvas);
      if (!layers.length) {
        setError("Aucune donnée réglementaire à importer pour cet UFI (pas de fiche d'étiquetage).");
        return;
      }
      rendreCompte(insertLayers(layers), `UFI ${cleaned}`);
      onClose();
    } catch (e) {
      setError(e.message || 'Impossible de récupérer la fiche.');
    } finally {
      setLoading(false);
    }
  };

  const handlePickProduit = (produit, clientName, gammeName, countryCode = null, langCode = null, clientId = null) => {
    setError(null);
    setLoading(true);
    try {
      // countryCode = le pays cliqué (FR/CH/BE…), langCode sa langue quand le pays
      // en a plusieurs (BE · nl) ; null = variante par défaut. L'identifiant du
      // client suit le bloc, pour le retrouver dans /catalog/clients/{id}.
      const payload = produitFiniToPayload(produit, clientName, gammeName, countryCode, langCode, clientId);
      const layers = buildRegulatoryLayers(payload, canvas);
      if (!layers.length) {
        setError(`« ${produit.name} » n'a pas de fiche d'étiquetage à importer${countryCode ? ` (${countryCode})` : ''}.`);
        return;
      }
      rendreCompte(insertLayers(layers), produit.name);
      onClose();
    } catch (e) {
      setError(e.message || 'Impossible d\'importer ce produit.');
    } finally {
      setLoading(false);
    }
  };

  return (
    // Backdrop SOMBRE en tous thèmes (modal-backdrop-strong) — sinon bg-black/X
    // est inversé en blanc sur thème clair. Pas de onClick de fermeture sur le
    // fond : la modale ne se ferme QUE via la croix (verrouillage anti-clic).
    <div className="fixed inset-0 z-[100] flex items-center justify-center modal-backdrop-strong backdrop-blur-sm p-4">
      <div className="w-full max-w-xl bg-forge-bg border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 bg-black/20 shrink-0">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-orange-500 shadow-[0_0_6px_rgba(249,115,22,0.7)]" />
            <h2 className="text-[10px] font-mono font-bold uppercase tracking-[0.2em] text-white/70">
              Importer une fiche réglementaire
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-md flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-colors"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Toggle UFI / Catalog */}
        <div className="px-5 pt-4 pb-2 shrink-0">
          <div className="flex bg-black/30 border border-white/5 rounded-lg p-1 gap-1">
            <button
              onClick={() => switchMode('ufi')}
              className={`flex-1 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-widest transition-all ${
                mode === 'ufi'
                  ? 'bg-orange-500 text-white shadow-sm'
                  : 'text-white/55 hover:text-white/85'
              }`}
            >
              Par UFI
            </button>
            <button
              onClick={() => switchMode('catalog')}
              className={`flex-1 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-widest transition-all ${
                mode === 'catalog'
                  ? 'bg-orange-500 text-white shadow-sm'
                  : 'text-white/55 hover:text-white/85'
              }`}
            >
              Par client
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto flex-1">
          {checkingToken && (
            <div className="mb-4 p-3 rounded-lg bg-white/[0.04] border border-white/10 text-[10px] font-mono text-white/50 flex items-center gap-2">
              <span className="w-3 h-3 border-2 border-white/15 border-t-white/60 rounded-full animate-spin shrink-0" />
              Vérification du jeton API FDS…
            </div>
          )}

          {noToken && (
            <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-[10px] font-mono text-amber-500">
              ⚠ Aucun jeton API FDS n'est configuré : ouvrez <strong>Préférences</strong> →
              <em> Récupération informations réglementaires</em> pour le renseigner avant d'importer.
            </div>
          )}

          {mode === 'ufi' && (
            <>
              <p className="text-[11px] font-sans text-white/60 leading-relaxed mb-4">
                Saisissez l'UFI du produit (format <span className="font-mono text-orange-500">XXXX-XXXX-XXXX-XXXX</span>) pour
                récupérer automatiquement les pictogrammes et mentions réglementaires.
              </p>

              <label className="block mb-4">
                <span className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-1 block">
                  UFI
                </span>
                <input
                  type="text"
                  value={ufi}
                  onChange={(e) => setUfi(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !loading) handleImportUfi(); }}
                  placeholder="21V4-ASV7-4J9K-E3JG"
                  autoFocus
                  disabled={noToken || checkingToken}
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-[12px] font-mono text-white/90 placeholder-white/25 outline-none focus:border-orange-500/40 transition-colors disabled:opacity-40 uppercase tracking-wider"
                />
              </label>

              {error && (
                <div className="mb-3 p-2.5 rounded-lg bg-red-500/10 border border-red-500/40 text-[10px] font-mono text-red-500">
                  ⚠ {error}
                </div>
              )}

              <button
                onClick={handleImportUfi}
                disabled={loading || noToken || checkingToken}
                className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 border border-orange-500 rounded-lg text-[11px] font-mono uppercase tracking-widest text-white shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Récupération…' : checkingToken ? 'Vérification du jeton…' : 'Importer la fiche'}
              </button>
            </>
          )}

          {mode === 'catalog' && (
            <>
              <p className="text-[11px] font-sans text-white/60 leading-relaxed mb-4">
                Parcourez l'arborescence <strong>client → gamme → produit fini</strong>.
                Dépliez un produit fini, puis cliquez sur la langue de l'étiquette pour importer
                son bloc réglementaire complet.
              </p>

              {error && (
                <div className="mb-3 p-2.5 rounded-lg bg-red-500/10 border border-red-500/40 text-[10px] font-mono text-red-500">
                  ⚠ {error}
                </div>
              )}

              {/* Arborescence affichée seulement quand le jeton est CONFIRMÉ : pendant
                  la vérification, elle se serait montrée vide (aucun chargement lancé)
                  et aurait fait croire à un catalogue sans clients. */}
              {tokenReady && (
                <>
                  <RegulatoryCatalogPicker
                    catalog={catalog}
                    onPick={handlePickProduit}
                    onExpandClient={loadClient}
                    clientStates={clientStates}
                    loading={catalogLoading}
                    error={catalogError}
                  />
                  {catalogError && !catalogLoading && (
                    <button
                      onClick={loadCatalog}
                      className="mt-3 w-full py-2 bg-orange-500 hover:bg-orange-600 border border-orange-500 rounded-lg text-[10px] font-mono uppercase tracking-widest text-white shadow-sm transition-all flex items-center justify-center gap-2"
                    >
                      {/* Icône SVG inline, comme partout ailleurs : un emoji ne suit ni la
                          couleur ni la taille du texte, et se rend différemment d'un poste
                          à l'autre. `aria-hidden` car le libellé dit déjà l'action. */}
                      <svg
                        width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
                        aria-hidden="true" className="shrink-0"
                      >
                        <polyline points="23 4 23 10 17 10" />
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                      </svg>
                      Réessayer le chargement
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
